/**
 * @prettier
 */

import React, { useState, useContext, useEffect } from 'react'
import ItemContext from '../../context/item/ItemContext'
import HomeContext from '../../context/home/HomeContext'
import StoreContext from '../../context/store/StoreContext'
import { withComponentLabel } from '../../utils/debug'

/**
 * Component for managing item-home and item-store relationships
 */
const ItemRelationships = ({ itemId }) => {
  const itemContext = useContext(ItemContext)
  const homeContext = useContext(HomeContext)
  const storeContext = useContext(StoreContext)

  const {
    getItemHomes,
    getItemStores,
    addItemHome,
    removeItemHome,
    addItemStore,
    removeItemStore,
  } = itemContext
  const { homes, getHomes } = homeContext
  const { stores, getStores } = storeContext

  const [itemHomes, setItemHomes] = useState([])
  const [itemStores, setItemStores] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedHome, setSelectedHome] = useState('')
  const [selectedStore, setSelectedStore] = useState('')

  useEffect(() => {
    const loadRelationships = async () => {
      await getHomes()
      await getStores()
      const homesData = await getItemHomes(itemId)
      const storesData = await getItemStores(itemId)
      setItemHomes(homesData || [])
      setItemStores(storesData || [])
      setLoading(false)
    }
    loadRelationships()
    // eslint-disable-next-line
  }, [itemId])

  const handleAddHome = async () => {
    if (!selectedHome) return
    try {
      await addItemHome(itemId, selectedHome)
      const updated = await getItemHomes(itemId)
      setItemHomes(updated)
      setSelectedHome('')
    } catch (err) {
      console.error('Error adding home:', err)
    }
  }

  const handleRemoveHome = async (homeId) => {
    try {
      await removeItemHome(itemId, homeId)
      const updated = await getItemHomes(itemId)
      setItemHomes(updated)
    } catch (err) {
      console.error('Error removing home:', err)
    }
  }

  const handleAddStore = async () => {
    if (!selectedStore) return
    try {
      await addItemStore(itemId, selectedStore)
      const updated = await getItemStores(itemId)
      setItemStores(updated)
      setSelectedStore('')
    } catch (err) {
      console.error('Error adding store:', err)
    }
  }

  const handleRemoveStore = async (storeId) => {
    try {
      await removeItemStore(itemId, storeId)
      const updated = await getItemStores(itemId)
      setItemStores(updated)
    } catch (err) {
      console.error('Error removing store:', err)
    }
  }

  if (loading) return <div className="p-4">Loading...</div>

  // Get available homes/stores (ones not already associated)
  const itemHomeIds = itemHomes.map((ih) => ih.home._id)
  const itemStoreIds = itemStores.map((is) => is.store._id)
  const availableHomes = homes.filter((h) => !itemHomeIds.includes(h._id))
  const availableStores = stores.filter((s) => !itemStoreIds.includes(s._id))

  return (
    <div className="space-y-6 p-4">
      {/* Homes Section */}
      <div className="border-2 border-gray-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-3">Homes</h3>

        {/* Display current homes */}
        <div className="mb-4">
          {itemHomes.length === 0 ? (
            <p className="text-gray-500 italic">No homes assigned</p>
          ) : (
            <div className="space-y-2">
              {itemHomes.map((itemHome) => (
                <div
                  key={itemHome._id}
                  className="flex items-center justify-between bg-gray-50 p-2 rounded"
                >
                  <span>{itemHome.home.name}</span>
                  <button
                    onClick={() => handleRemoveHome(itemHome.home._id)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add home selector */}
        {availableHomes.length > 0 && (
          <div className="flex gap-2">
            <select
              value={selectedHome}
              onChange={(e) => setSelectedHome(e.target.value)}
              className="flex-1 border-2 border-gray-300 rounded px-3 py-2"
            >
              <option value="">Select a home...</option>
              {availableHomes.map((home) => (
                <option
                  key={home._id}
                  value={home._id}
                >
                  {home.name}
                </option>
              ))}
            </select>
            <button
              onClick={handleAddHome}
              disabled={!selectedHome}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Add
            </button>
          </div>
        )}
      </div>

      {/* Stores Section */}
      <div className="border-2 border-gray-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-3">Stores</h3>

        {/* Display current stores */}
        <div className="mb-4">
          {itemStores.length === 0 ? (
            <p className="text-gray-500 italic">No stores assigned</p>
          ) : (
            <div className="space-y-2">
              {itemStores.map((itemStore) => (
                <div
                  key={itemStore._id}
                  className="flex items-center justify-between bg-gray-50 p-2 rounded"
                >
                  <span>{itemStore.store.name}</span>
                  <button
                    onClick={() => handleRemoveStore(itemStore.store._id)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add store selector */}
        {availableStores.length > 0 && (
          <div className="flex gap-2">
            <select
              value={selectedStore}
              onChange={(e) => setSelectedStore(e.target.value)}
              className="flex-1 border-2 border-gray-300 rounded px-3 py-2"
            >
              <option value="">Select a store...</option>
              {availableStores.map((store) => (
                <option
                  key={store._id}
                  value={store._id}
                >
                  {store.name}
                </option>
              ))}
            </select>
            <button
              onClick={handleAddStore}
              disabled={!selectedStore}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Add
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default withComponentLabel(ItemRelationships, '🔗 ItemRelationships')
